import { useState, useEffect, useRef } from "react";
import {
	refreshCustomerCreditDetailsFromUser,
	fetchMembershipClientCreditPatch,
} from "../../Utils/customerCreditRefresh";
import styles from "../RightSidebar/rightsidebar.module.css";
import { ImageLink } from "../Buttons/ImageLink";
import BackBtn from "../../Assets/images/back.svg";
import PrimaryButton from "../../Components/Buttons/PrimaryButton";
import BorderedButton from "../Buttons/BorderedButton";
import { useGlobalContext } from "../../Context/GlobalContext";
import BookingPaymentDetails from "../BookingPaymentDetails/BookingPaymentDetails";
import { InputValidation, roundCashAmount } from "../../Utils/utils";
import MembershipPasswordPopup from "../Popups/MembershipPasswordPopup/MembershipPasswordPopup";
import SetMembershipPasswordPopup from "../Popups/MembershipPasswordPopup/SetMembershipPasswordPopup";
import MembershipNoPasswordPopup from "../Popups/MembershipPasswordPopup/MembershipNoPasswordPopup";
import SkipPasswordForMembership from "../Popups/MembershipPasswordPopup/SkipPasswordForMembership";
import CustomModal from "../Modal/CustomModal";
import MembershipClientDropdown from "../DropDown/MembershipClientDropdown/MembershipClientDropdown";
import {
	fetchAndStoreCheckoutCustomerPasswordFlag,
	getCheckoutCustomerIsPasswordSet,
	setCheckoutCustomerIsPasswordSet,
} from "../../Utils/customerPasswordSession";
import { isMembershipExpired, resolveMembershipIdForApi } from "../../Utils/customerSearchUtils";
import "../Popups/popup.common.css";
import { ERROR_MESSAGES } from "../../Utils/Constants/errorMessages";
import CustomerCreditDetails from "../CustomerCreditDetails/CustomerCreditDetails";
import ReloadIcon from "../Icons/ReloadIcon";
import {
	findOpenMembershipSaleOrPendingTransactionForCustomer,
} from "../../Utils/openMembershipSale";

const isMembershipPasswordConfigured = (rawFlag) =>
	!(rawFlag === false || rawFlag === 0 || rawFlag === "0");

/** Block Others Membership payment when the member is missing, expired, or has no usable credit. */
const getOthersMembershipPaymentBlockReason = (client, chargeAmount) => {
	if (client?.user_id == null || client?.user_id === "") {
		return ERROR_MESSAGES.SELECT_MEMBERSHIP_CLIENT;
	}
	if (isMembershipExpired(client)) {
		return ERROR_MESSAGES.MEMBERSHIP_EXPIRED;
	}
	const openingBal = Number(
		client?.credit_balance ?? client?.current_credit_balance ?? 0
	);
	const balance = Number.isFinite(openingBal) ? openingBal : 0;
	const chargeVal = Number(chargeAmount);
	const chargeSafe = Number.isFinite(chargeVal) ? chargeVal : 0;
	if (!(balance > 0) || chargeSafe > balance) {
		return ERROR_MESSAGES.INSUFFICIENT_CASHLESS_BALANCE;
	}
	return "";
};

const getBookingUserRecord = (bookingDetails) => {
	const user = bookingDetails?.user;
	if (!user) return null;
	if (Array.isArray(user)) return user[0] ?? null;
	return typeof user === "object" ? user : null;
};

/** Parse cash input string to integer cents (avoids floating-point drift). Returns null if not a usable amount. */
const parseCashInputToCents = (raw) => {
	const s = String(raw ?? "").trim();
	if (!s || s === ".") return null;
	if (!/^\d*\.?\d{0,2}$/.test(s)) return null;
	const parts = s.split(".");
	const wholeRaw = parts[0] === "" ? "0" : parts[0];
	if (!/^\d+$/.test(wholeRaw)) return null;
	let frac = parts.length > 1 ? parts[1] : "";
	if (frac.length > 2) return null;
	frac = (frac + "00").slice(0, 2);
	return Number(wholeRaw) * 100 + Number(frac);
};

/** Convert a dollar amount (number) to cents using its 2-decimal string form. */
const amountDueToCents = (due) => {
	if (typeof due !== "number" || isNaN(due) || due < 0) return 0;
	const [w, f] = due.toFixed(2).split(".");
	return Number(w) * 100 + Number(f);
};

const ConfirmationPage = ({
	summary,
	onBack,
	onReload = null,
	onConfirm,
	onCancel,
	hideHeader = false,
	isProductSale = false,
	bookingID = null,
	isMembershipPayment = false,
	onMembershipClientTopUpClick = null,
	onOpenMembershipSale = null,
	membershipListUserId = null,
	embeddedMembershipCheckout = false,
	embeddedMembershipHomePage = false,
	customerCreditRefreshKey = 0,
	creditFetchUserId = null,
	selectedOthersMembershipClient = null,
	onOthersMembershipClientChange = null,
}) => {
	const {
		amountDue,
		currentPaymentMethod,
		currentPaymentMethodId,
		currentPaymentMethodTitle,
		reference,
		setReference,
		amountToCharge,
		setAmountToCharge,
		bookingDetails,
		customerCreditDetails,
		setCustomerCreditDetails,
		customerData,
		setCustomerData,
	} = useGlobalContext();

	const bookingId = isProductSale ? (bookingID || null) : (bookingID ?? bookingDetails?.id);
	const resolvedMembershipListUserId =
		membershipListUserId ?? bookingDetails?.user?.id ?? null;
	const resolvedCreditUserId =
		creditFetchUserId ?? bookingDetails?.user?.id ?? customerData?.id ?? null;

	const [errorReceivedAmount, setErrorReceivedAmount] = useState("");
	const [receivedCashAmount, setReceivedCashAmount] = useState("");
	const receivedCashInputRef = useRef(null);
	const referenceInputRef = useRef(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [hasPaymentDetails, setHasPaymentDetails] = useState(false);
	const [showMembershipPasswordModal, setShowMembershipPasswordModal] = useState(false);
	const [showSetMembershipPasswordModal, setShowSetMembershipPasswordModal] = useState(false);
	const [showNoPasswordPopup, setShowNoPasswordPopup] = useState(false);
	const [showSkipPasswordModal, setShowSkipPasswordModal] = useState(false);
	const [internalOthersMembershipClient, setInternalOthersMembershipClient] = useState(null);
	const isControlledOthersMembershipClient =
		typeof onOthersMembershipClientChange === "function";
	const othersMembershipClient = isControlledOthersMembershipClient
		? selectedOthersMembershipClient
		: internalOthersMembershipClient;
	const setOthersMembershipClient = isControlledOthersMembershipClient
		? onOthersMembershipClientChange
		: setInternalOthersMembershipClient;

	const handleOthersMembershipClientSelect = (client) => {
		setOthersMembershipClient(client);
		const hasClient = client?.user_id != null && client?.user_id !== "";
		// Clear validation errors that no longer apply when the member is picked or removed.
		// Insufficient-balance clears on both select and delete; "select a client" clears
		// only once a valid client is chosen.
		setError((prev) => {
			if (
				prev === ERROR_MESSAGES.INSUFFICIENT_CASHLESS_BALANCE ||
				prev === ERROR_MESSAGES.MEMBERSHIP_EXPIRED
			) {
				return "";
			}
			if (hasClient && prev === ERROR_MESSAGES.SELECT_MEMBERSHIP_CLIENT) return "";
			return prev;
		});
	};
	const CashPaymentMethod = currentPaymentMethod === "cash";
	const membershipCreditPayment =
		currentPaymentMethod === "cashless";
	const otherMembershipCreditPayment =
		currentPaymentMethod === "other_membership";
	const showReferenceInput =
		!CashPaymentMethod && !membershipCreditPayment && !otherMembershipCreditPayment;
	const requiresMembershipPasswordStep =
		membershipCreditPayment || otherMembershipCreditPayment;

	useEffect(() => {
		if (!resolvedCreditUserId || !customerCreditRefreshKey) return;

		const refreshOthersClientBalance = async () => {
			const patch = await fetchMembershipClientCreditPatch(resolvedCreditUserId);
			if (!patch) return;
			setOthersMembershipClient((previous) => ({
				...(previous || {}),
				...patch,
			}));
		};

		if (
			otherMembershipCreditPayment &&
			othersMembershipClient?.user_id != null &&
			String(othersMembershipClient.user_id) === String(resolvedCreditUserId)
		) {
			refreshOthersClientBalance();
			return;
		}

		refreshCustomerCreditDetailsFromUser(resolvedCreditUserId, setCustomerCreditDetails);
	}, [
		resolvedCreditUserId,
		customerCreditRefreshKey,
		setCustomerCreditDetails,
		otherMembershipCreditPayment,
		othersMembershipClient?.user_id,
		setOthersMembershipClient,
	]);

	const { selectedOutlet, loggedOutletID } = useGlobalContext();
	const [openMembershipSale, setOpenMembershipSale] = useState(null);
	const [othersOpenMembershipSale, setOthersOpenMembershipSale] = useState(null);

	// Detect open/partial membership sale for the selected payment client
	useEffect(() => {
		if (!membershipCreditPayment || !resolvedCreditUserId) {
			setOpenMembershipSale(null);
			return;
		}
		let cancelled = false;
		const check = async () => {
			try {
				const membershipDetail =
					customerCreditDetails?.[0]?.membership_detail ||
					customerCreditDetails?.[0]?.membershipDetail ||
					customerCreditDetails?.[0] ||
					null;
				const sale = await findOpenMembershipSaleOrPendingTransactionForCustomer(
					resolvedCreditUserId,
					selectedOutlet || loggedOutletID || null,
					membershipDetail
				);
				if (!cancelled) setOpenMembershipSale(sale);
			} catch {
				if (!cancelled) setOpenMembershipSale(null);
			}
		};
		check();
		return () => { cancelled = true; };
	}, [membershipCreditPayment, resolvedCreditUserId, selectedOutlet, loggedOutletID, customerCreditDetails]);

	// Detect open/partial membership sale for the selected Others Membership client
	useEffect(() => {
		const userId = othersMembershipClient?.user_id;
		if (!otherMembershipCreditPayment || !userId) {
			setOthersOpenMembershipSale(null);
			return;
		}
		let cancelled = false;
		const check = async () => {
			try {
				const membershipDetail =
					othersMembershipClient?.membership_detail ||
					othersMembershipClient?.membershipDetail ||
					othersMembershipClient ||
					null;
				const sale = await findOpenMembershipSaleOrPendingTransactionForCustomer(
					userId,
					selectedOutlet || loggedOutletID || null,
					membershipDetail
				);
				if (!cancelled) setOthersOpenMembershipSale(sale);
			} catch {
				if (!cancelled) setOthersOpenMembershipSale(null);
			}
		};
		check();
		return () => { cancelled = true; };
	}, [otherMembershipCreditPayment, othersMembershipClient, selectedOutlet, loggedOutletID]);

	const resolveMembershipIdForPassword = () => {
		if (otherMembershipCreditPayment && othersMembershipClient) {
			return resolveMembershipIdForApi(othersMembershipClient);
		}
		const bookingUser = getBookingUserRecord(bookingDetails);
		return (
			resolveMembershipIdForApi(customerCreditDetails?.[0]) ||
			resolveMembershipIdForApi(customerData) ||
			resolveMembershipIdForApi(bookingUser)
		);
	};

	const resolveMembershipUserIdForPassword = () => {
		if (otherMembershipCreditPayment && othersMembershipClient?.user_id != null) {
			return othersMembershipClient.user_id;
		}
		const bookingUser = getBookingUserRecord(bookingDetails);
		return bookingUser?.id ?? customerData?.id ?? null;
	};

	useEffect(() => {
		if (CashPaymentMethod) {
		const updatedamountToCharge = roundCashAmount(amountToCharge);
		if (updatedamountToCharge !== amountToCharge) {
			setAmountToCharge(updatedamountToCharge);
		}
	}
}, [amountToCharge, currentPaymentMethod, setAmountToCharge]);


	useEffect(() => {
		if (!CashPaymentMethod) {
			setReceivedCashAmount("");
			setErrorReceivedAmount("");
			return;
		}
		if (!amountToCharge) {
			setReceivedCashAmount("");
			setErrorReceivedAmount("");
			return;
		}
		setReceivedCashAmount("");
		setErrorReceivedAmount("");
	}, [currentPaymentMethod, amountToCharge]);

	useEffect(() => {
		if (!CashPaymentMethod) return;
		const id = requestAnimationFrame(() => {
			receivedCashInputRef.current?.focus();
		});
		return () => cancelAnimationFrame(id);
	}, [CashPaymentMethod]);

	useEffect(() => {
		if (!showReferenceInput) return;
		const id = requestAnimationFrame(() => {
			const input = referenceInputRef.current;
			if (!input) return;
			input.focus({ preventScroll: true });
			if (input.value) {
				input.select();
			}
		});
		return () => cancelAnimationFrame(id);
	}, [showReferenceInput, currentPaymentMethod]);

	// const getInitialReceivedAmount = () => {
	// 	const pendingAmount = parseFloat(amountToCharge);
	// 	if (!isNaN(pendingAmount) && pendingAmount > 0) {
	// 		return pendingAmount.toFixed(2);
	// 	}
	// 	const parsed = parseFloat(amountDue);
	// 	if (isNaN(parsed)) return "0.00";
	// 	if (CashPaymentMethod) {
	// 		const rounded = roundCashAmount(parsed);
	// 		return rounded.toFixed(2);
	// 	}
	// 	return parsed.toFixed(2);
	// };

	const [receivedAmount, setReceivedAmount] = useState('');

	const [error, setError] = useState("");
	const [paymentError, setPaymentError] = useState("");
	const paymentErrorResponseRef = useRef(null);

	const [referenceError, setReferenceError] = useState("");
	const parsedAmountToCharge = parseFloat(amountToCharge);
	const parsedAmountDue = parseFloat(amountDue);
	const effectiveAmountDue =
		!isNaN(parsedAmountToCharge) && parsedAmountToCharge > 0
			? parsedAmountToCharge
			: (!isNaN(parsedAmountDue) ? parsedAmountDue : 0);
			
	const normalizedReceivedCashAmount = String(receivedCashAmount ?? "");
	const receivedCashCents = parseCashInputToCents(normalizedReceivedCashAmount);
	const dueCents = amountDueToCents(effectiveAmountDue);

	const isCashAmountReceivedInvalid = receivedCashCents === null || receivedCashCents <= 0;
	const isInsufficientCash =
		!isCashAmountReceivedInvalid && receivedCashCents < dueCents;
	const cashChangeDueCents = !isCashAmountReceivedInvalid ? receivedCashCents - dueCents : 0;
	const cashChangeDueDisplay = (cashChangeDueCents / 100).toFixed(2);

	const cashReceivedErrorMessage = errorReceivedAmount;

	const isCashConfirmDisabled =
		isSubmitting || isCashAmountReceivedInvalid || isInsufficientCash;

	const extractReferenceError = (payload) => {
		// Booking payments return errors.reference; membership purchase/top-up
		// (/memberships/purchase-topup) returns errors.payment_reference.
		const referenceErrors =
			payload?.errors?.reference || payload?.errors?.payment_reference ||
			payload?.data?.errors?.reference || payload?.data?.errors?.payment_reference ||
			payload?.response?.data?.errors?.reference || payload?.response?.data?.errors?.payment_reference;
		if (Array.isArray(referenceErrors) && referenceErrors.length > 0) {
			return referenceErrors[0];
		}
		if (typeof referenceErrors === "string" && referenceErrors.trim()) {
			return referenceErrors;
		}
		return "";
	};

	const extractApiMessage = (payload) =>
		payload?.message || payload?.data?.message || payload?.response?.data?.message || "";

	useEffect(() => {
		const pendingAmount = parseFloat(amountToCharge);
		if (!isNaN(pendingAmount) && pendingAmount > 0) {
			setReceivedAmount(pendingAmount.toFixed(2));
			return;
		}
		const parsed = parseFloat(amountDue);
		if (isNaN(parsed)) {
			setReceivedAmount("0.00");
			return;
		}
		if (CashPaymentMethod) {
			const rounded = roundCashAmount(parsed);
			setReceivedAmount(rounded.toFixed(2));
		} else {
			setReceivedAmount(parsed.toFixed(2));
		}
	}, [amountDue, currentPaymentMethod, amountToCharge]);

	useEffect(() => {
		if (!requiresMembershipPasswordStep) {
			setShowMembershipPasswordModal(false);
			setShowSetMembershipPasswordModal(false);
		}
	}, [currentPaymentMethod, requiresMembershipPasswordStep]);

	useEffect(() => {
		if (!otherMembershipCreditPayment) {
			setOthersMembershipClient(null);
		}
	}, [currentPaymentMethod, otherMembershipCreditPayment]);


	const handleConfirm = async (
		fromMembershipPasswordModal = false,
		{
			skipPassword: skipPasswordForRequest,
			skipPasswordReason: skipPasswordReasonForRequest = "",
		} = {}
	) => {
		if (isSubmitting) {
			return;
		}

		if (otherMembershipCreditPayment && !fromMembershipPasswordModal) {
			const blockReason = getOthersMembershipPaymentBlockReason(
				othersMembershipClient,
				amountToCharge
			);
			if (blockReason) {
				setError(blockReason);
				return;
			}
		}

		if (requiresMembershipPasswordStep && !fromMembershipPasswordModal) {
			const bookingUser = getBookingUserRecord(bookingDetails);
			const checkoutCustomerId = otherMembershipCreditPayment
				? othersMembershipClient?.user_id
				: bookingUser?.id ?? customerData?.id;

			// Always check the API for the latest is_password_set value
			let isPasswordSet = false;
			if (checkoutCustomerId != null) {
				const fromApi = await fetchAndStoreCheckoutCustomerPasswordFlag(checkoutCustomerId);
				if (fromApi !== undefined) {
					isPasswordSet = fromApi;
				} else if (otherMembershipCreditPayment) {
					isPasswordSet = isMembershipPasswordConfigured(
						othersMembershipClient?.is_password_set
					);
				} else {
					const localPasswordFlag =
						customerData?.is_password_set ?? bookingUser?.is_password_set;
					isPasswordSet = localPasswordFlag !== undefined && localPasswordFlag !== null
						? isMembershipPasswordConfigured(localPasswordFlag)
						: false;
				}
			}

			if (otherMembershipCreditPayment && !isPasswordSet) {
				setShowNoPasswordPopup(true);
				return;
			} else if (!isPasswordSet) {
				setShowSetMembershipPasswordModal(true);
				return;
			} else {
				setShowMembershipPasswordModal(true);
				return;
			}
		}
		if (fromMembershipPasswordModal && otherMembershipCreditPayment) {
			const blockReason = getOthersMembershipPaymentBlockReason(
				othersMembershipClient,
				amountToCharge
			);
			if (blockReason) {
				setError(blockReason);
				setShowMembershipPasswordModal(false);
				return;
			}
		}
		setIsSubmitting(true);
		setReferenceError("");
		setPaymentError("");
		paymentErrorResponseRef.current = null;

		try {
		const raw = parseFloat(receivedAmount);
		let due = parseFloat(amountDue);
		const trimmedReference = (reference || "").trim();
		const sanitizedReference = InputValidation.removeEmoji(trimmedReference);
		if (sanitizedReference !== trimmedReference) {
			setReference(sanitizedReference);
		}
		
		// Apply cash rounding to due amount for comparison if payment method is CASH
		if (CashPaymentMethod && !isNaN(due)) {
			due = roundCashAmount(due);
		}

		const confirmedAmount = CashPaymentMethod ? effectiveAmountDue : raw;

		if (!confirmedAmount || confirmedAmount <= 0) {
			setError("Enter a valid amount.");
			setIsSubmitting(false); // reset since no API call
			return;
		}

		if (CashPaymentMethod) {
			if (isCashAmountReceivedInvalid) {
				setErrorReceivedAmount("Enter a valid Amount Received.");
				setIsSubmitting(false);
				return;
			}
			if (isInsufficientCash) {
				setIsSubmitting(false);
				return;
			}
		}
		if (
			otherMembershipCreditPayment &&
			(othersMembershipClient?.user_id == null || othersMembershipClient?.user_id === "")
		) {
			setError(ERROR_MESSAGES.SELECT_MEMBERSHIP_CLIENT);
			setIsSubmitting(false);
			return;
		}
		const othersMemberPasswordNotSet =
			otherMembershipCreditPayment &&
			!isMembershipPasswordConfigured(othersMembershipClient?.is_password_set);

		const skipPasswordValue =
			othersMemberPasswordNotSet
				? 1
				: skipPasswordForRequest !== undefined
					? skipPasswordForRequest
						? 1
						: 0
					: 0;

		const skipPasswordReasonValue =
			skipPasswordValue === 1
				? String(skipPasswordReasonForRequest ?? "").trim()
				: "";

		const othersSelectedUserId =
			otherMembershipCreditPayment && othersMembershipClient?.user_id != null
				? othersMembershipClient.user_id
				: undefined;
		const membershipIdForPayment = requiresMembershipPasswordStep
			? resolveMembershipIdForPassword()
			: null;
		const confirmPayloadExtras = {
			...(othersSelectedUserId != null && othersSelectedUserId !== ""
				? { selectedUserId: othersSelectedUserId }
				: {}),
			...(membershipIdForPayment != null
				? { membershipId: membershipIdForPayment }
				: {}),
		};

		if (confirmedAmount < due - 0.009) {
			const result = await onConfirm({
			method: currentPaymentMethod,
			payment_method_id: currentPaymentMethodId,
			amount: confirmedAmount,
			partial: true,
			reference: sanitizedReference,
			skip_password: skipPasswordValue,
			skip_password_reason: skipPasswordReasonValue,
			...confirmPayloadExtras,
			});
			const apiError = extractReferenceError(result);
			const isFailed = result && (
				result.success === false ||
				result.status === false ||
				result?.data?.success === false ||
				result?.data?.status === false
			);
			if (isFailed && apiError) {
				setReferenceError(apiError);
				return;
			}
			if (isFailed) {
				paymentErrorResponseRef.current = result;
				setPaymentError(extractApiMessage(result) || "Payment failed. Please reload and try again.");
				return;
			}
		} else {
			const result = await onConfirm({
			method: currentPaymentMethod,
			payment_method_id: currentPaymentMethodId,
			amount: confirmedAmount,
			partial: false,
			reference: sanitizedReference,
			skip_password: skipPasswordValue,
			skip_password_reason: skipPasswordReasonValue,
			...confirmPayloadExtras,
			});
			const apiError = extractReferenceError(result);
			const isFailed = result && (
				result.success === false ||
				result.status === false ||
				result?.data?.success === false ||
				result?.data?.status === false
			);
			if (isFailed && apiError) {
				setReferenceError(apiError);
				return;
			}
			if (isFailed) {
				paymentErrorResponseRef.current = result;
				setPaymentError(extractApiMessage(result) || "Payment failed. Please reload and try again.");
				return;
			}
		}

		if (fromMembershipPasswordModal) {
			setShowMembershipPasswordModal(false);
		}
		setOthersMembershipClient(null);
		setError("");
		setReference("");
		setAmountToCharge("");
		} catch (submitError) {
			const apiError = extractReferenceError(submitError);
			if (apiError) {
				setReferenceError(apiError);
				return;
			}
			paymentErrorResponseRef.current = submitError;
			setPaymentError(extractApiMessage(submitError) || "Failed to process payment.");
		} finally {
		setIsSubmitting(false); // always re-enable
		}
	};

	const handleCancelClick = () => {
		setOthersMembershipClient(null);
		onBack?.();
	};

	// After useEffect
	if (!summary) return <p>Missing payment details</p>;

	const othersMembershipBlockReason = otherMembershipCreditPayment
		? getOthersMembershipPaymentBlockReason(othersMembershipClient, amountToCharge)
		: "";
	const othersOpeningBalance = Number(othersMembershipClient?.credit_balance ?? othersMembershipClient?.current_credit_balance ??0);
	const othersBalance = Number.isFinite(othersOpeningBalance) ? othersOpeningBalance : 0;
	const othersCharge = Number(amountToCharge);
	const othersChargeSafe = Number.isFinite(othersCharge) ? othersCharge : 0;
	const othersMembershipVisibleReason =
		otherMembershipCreditPayment && othersMembershipClient && (!(othersBalance > 0) || othersChargeSafe > othersBalance)? ERROR_MESSAGES.INSUFFICIENT_CASHLESS_BALANCE
			: "";
	const visibleConfirmError =
		error === ERROR_MESSAGES.MEMBERSHIP_EXPIRED ||
		error === ERROR_MESSAGES.INSUFFICIENT_CASHLESS_BALANCE ? "": error;

		const isConfirmDisabled =
		currentPaymentMethod === "cash"
			? isCashConfirmDisabled
			: isSubmitting ||
				(requiresMembershipPasswordStep && showMembershipPasswordModal) ||
				(requiresMembershipPasswordStep && showSetMembershipPasswordModal) ||
				Boolean(othersMembershipBlockReason);

	return (
		<div
			className={`${styles.confirmationPage} h-100 ${embeddedMembershipCheckout ? styles.membershipEmbeddedConfirmation : ''} ${embeddedMembershipCheckout && embeddedMembershipHomePage ? styles.membershipEmbeddedConfirmationHome : ''}`}
		>
			<div className={styles.rightHeader}>
				<div className={styles.dFlex}>
					<ImageLink
						imageSrc={BackBtn}
						altText="Link"
						handleClick={onBack}
					/>
					<h5>Payments</h5>
				</div>
			</div>
			<div
				className={`${styles.infoWrapper} ${styles.paymentContent}`}
				style={paymentError && !embeddedMembershipCheckout ? { paddingBottom: 70 } : undefined}
			>
				{/* Payment Summary */}
				<div className={styles.sidebarContainer}>
					<div className={styles.sidebarRow}>
						<div className={`${styles.sidebarCellLeft} ${styles.sidebarText}`}>
							Subtotal
						</div>
						<div className={`${styles.sidebarCellRight} ${styles.sidebarText}`}>
							${summary?.subtotal?.toFixed(2)}
						</div>
					</div>
					{!isMembershipPayment && (
						<div className={styles.sidebarRow}>
							<div className={`${styles.sidebarCellLeft} ${styles.sidebarText}`}>
								GST
							</div>
							<div className={`${styles.sidebarCellRight} ${styles.sidebarText}`}>
								${parseFloat(summary.gst)?.toFixed(2)}
							</div>
						</div>
					)}
					<div className={styles.sidebarRow}>
						<div className={`${styles.sidebarCellLeft} ${styles.sidebarTextBold}`}>
							Total
						</div>
						<div className={`${styles.sidebarCellRight} ${styles.sidebarTextBold}`}>
							${parseFloat(summary.total).toFixed(2)}
						</div>
					</div>
				</div>

				{hasPaymentDetails && (
					<>
					<hr className={styles.divider} />
					<BookingPaymentDetails
						bookingID={bookingId}
						from={isProductSale ? "sale-booking-info" : "home"}
						onPaymentDetailsChange={setHasPaymentDetails}
					/>
					</>
				)}

		
				<hr className={styles.divider} />

				{CashPaymentMethod ? 
				<div className={styles.sidebarContainer}>
					<div className={`${styles.sidebarCellLeft} ${styles.sidebarTextBold}`}>Calculate Cash Change</div>
					<div className={styles.sidebarRow}>
						<div className={`${styles.sidebarCellLeft} ${styles.sidebarText}`}>Amount Due</div>
						<div className={`${styles.sidebarCellRight} ${styles.sidebarText}`}>${effectiveAmountDue.toFixed(2)}</div>
					</div>
					<div className={styles.sidebarRow}>
						<div className={`${styles.sidebarCellLeft} ${styles.sidebarText}`}>Amount Received</div>
						<div className={`${styles.sidebarCellRight} ${styles.sidebarText}`}>
							<div
								className={`${styles.tipInputWrapper} ${cashReceivedErrorMessage ? styles.tipInputWrapperError : ""}`}
							>
								<span className={styles.dollarPrefix}>$</span>
								<input
									ref={receivedCashInputRef}
									type="text"
									className={styles.tipInput}
									placeholder="0.00"
									value={receivedCashAmount}
									aria-invalid={cashReceivedErrorMessage ? "true" : "false"}
									onChange={(e) => {
										const val = e.target.value;
										if (!/^\d*\.?\d{0,2}$/.test(val)) return;
										setReceivedCashAmount(val);
										if (errorReceivedAmount) {
											setErrorReceivedAmount("");
										}
									}}
									onBlur={(e) => {
										const val = e.target.value.trim();
										if (!val) {
											setReceivedCashAmount("");
											setErrorReceivedAmount("");
											return;
										}
										const cents = parseCashInputToCents(val);
										if (cents === null || cents <= 0) {
											setErrorReceivedAmount("Enter a valid Amount Received.");
											return;
										}
										const whole = Math.floor(cents / 100);
										const frac = String(cents % 100).padStart(2, "0");
										setReceivedCashAmount(`${whole}.${frac}`);
										if (cents < dueCents) {
											setErrorReceivedAmount("The amount received must be at least the amount due.");
										} else {
											setErrorReceivedAmount("");
										}
									}}
								/>
							</div>
						</div>
					</div>
					<div className={styles.sidebarRow}>
						<div className={`${styles.sidebarCellLeft} ${styles.sidebarText}`}>Change Due</div>
						<div className={`${styles.sidebarCellRight} ${styles.sidebarText}`}>${cashChangeDueDisplay}</div>
					</div>
					{cashReceivedErrorMessage && (
						<div className={`${styles.errorMessage} pl-0 mt-0`}>{cashReceivedErrorMessage}</div>
					)}
				</div> 
				:
				membershipCreditPayment ?
				<div className={styles.sidebarContainer}>
					<div className={`${styles.sidebarCellLeft} ${styles.sidebarTextBold}`}>
						<span className="text-capitalize">{currentPaymentMethodTitle.toLowerCase()} Payment type</span>
					</div>
					<div className={`${styles.sidebarCellLeft} ${styles.sidebarText} ${styles.paymentCustomerDetails}`}>
						{customerCreditDetails?.[0]?.user_name} (#{customerCreditDetails?.[0]?.membership_number})
					</div>
					<div className={`${styles.sidebarCellLeft} ${styles.sidebarText}`}>
						Payment of ${amountToCharge} will be reduced from credit balance
					</div>

					<CustomerCreditDetails
						customerCreditDetails={customerCreditDetails}
						isLoading={false}
						showTopUpAction={typeof onMembershipClientTopUpClick === "function" && !openMembershipSale}
						onTopUpClick={(flowType) =>
							onMembershipClientTopUpClick?.(flowType, othersMembershipClient)
						}
						hasOpenMembershipSale={Boolean(openMembershipSale)}
						openMembershipSale={openMembershipSale}
						onOpenMembershipSale={() => {
							if (typeof onOpenMembershipSale === 'function') {
								onOpenMembershipSale(openMembershipSale);
							} else {
								onMembershipClientTopUpClick?.('topup', customerCreditDetails?.[0]);
							}
						}}
						openMembershipSaleLabel="Go to Open Sale"
					/>
				</div> 
				:
				otherMembershipCreditPayment ?
				<div className={styles.sidebarContainer}>
					<div className={`${styles.sidebarCellLeft} ${styles.sidebarTextBold}`}>
						<span className="text-capitalize">{currentPaymentMethodTitle.toLowerCase()} Payment type</span>
					</div>
					<MembershipClientDropdown
						selectedClient={othersMembershipClient}
						onSelect={handleOthersMembershipClientSelect}
						amountToCharge={amountToCharge}
						onTopUpClick={(flowType, client) =>
							onMembershipClientTopUpClick?.(flowType, client ?? othersMembershipClient)
						}
						hasOpenMembershipSale={Boolean(othersOpenMembershipSale)}
						onOpenMembershipSale={() => {
							if (typeof onOpenMembershipSale === 'function') {
								onOpenMembershipSale(othersOpenMembershipSale, othersMembershipClient);
							} else {
								onMembershipClientTopUpClick?.('topup', othersMembershipClient);
							}
						}}
						openMembershipSaleLabel="Go to Open Sale"
						user={resolvedMembershipListUserId}
					/>
				</div> 
				:
				<div className={styles.sidebarContainer}>
					<div className={`${styles.sidebarCellLeft} ${styles.sidebarTextBold}`}>
					<span className="text-capitalize">{currentPaymentMethodTitle.toLowerCase()} Payment type</span>
					</div>
					<div className={`${styles.sidebarCellLeft} ${styles.sidebarText}`}>
						Type in an optional reference for ${amountToCharge}
					</div>
					<div className={styles.sidebarCellLeft}>
						<p>Reference</p>
						<input
							ref={referenceInputRef}
							type="text"
							className={`${styles.formControl} ${styles.referenceInput}`}
							placeholder="e.g. Transaction #"
							value={reference}
							onFocus={(e) => {
								if (e.target.value) {
									e.target.select();
								}
							}}
							onChange={(e) => {
								const value = e.target.value;
								if (InputValidation.hasEmoji(value)) {
									return;
								}
								setReference(value);
								if (referenceError) setReferenceError("");
							}}
						/>
						{referenceError && <p className={styles.errorMessage}>{referenceError}</p>}
					</div>
				</div>
				}

				{(visibleConfirmError || othersMembershipVisibleReason) && <div className={styles.sidebarContainer}>
					<p className={`${styles.errorMessage} pl-0 mt-0`}>
						{visibleConfirmError || othersMembershipVisibleReason}
					</p>
				</div>
				}

				{paymentError && (
					<div className={embeddedMembershipCheckout ? styles.sidebarContainer : styles.overlappingErrorContainer}>
						<div className={styles.saleInProgressErrorRow}>
							<div className={styles.errorMessage}>{paymentError}</div>
							{onReload && (
								<button
									type="button"
									className={styles.reloadErrorButton}
									onClick={() => onReload(paymentErrorResponseRef.current)}
								>
									<ReloadIcon size={12} color="currentColor" />
									Reload
								</button>
							)}
						</div>
					</div>
				)}

				<div
					className={`button-container ${styles.formBtnContainer}`}>
					<BorderedButton text="Cancel" onClick={handleCancelClick} />
					<PrimaryButton
						text={isSubmitting ? "Processing..." : "Confirm"}
						onClick={() => handleConfirm(false)}
						disabled={isConfirmDisabled}
					/>
				</div>
			</div>
			{showSetMembershipPasswordModal && (
				<SetMembershipPasswordPopup
					membershipId={resolveMembershipIdForPassword()}
					showSkipForNow={true}
					showSuccessToast={false}
					onClose={() => setShowSetMembershipPasswordModal(false)}
					onSuccess={() => {
						const bookingUser = getBookingUserRecord(bookingDetails);
						const customerId = otherMembershipCreditPayment
							? othersMembershipClient?.user_id
							: bookingUser?.id ?? customerData?.id;
						setCheckoutCustomerIsPasswordSet(customerId, true);
						setCustomerData((prev) => ({
							...(prev || {}),
							is_password_set: true,
						}));
						setOthersMembershipClient((prev) =>
							prev ? { ...prev, is_password_set: true } : prev
						);
						setShowSetMembershipPasswordModal(false);
						setShowMembershipPasswordModal(false);
						handleConfirm(true, {
							skipPassword: false,
							skipPasswordReason: "",
						});
					}}
					onSkip={() => {
						setShowSetMembershipPasswordModal(false);
						if (otherMembershipCreditPayment) {
							setShowSkipPasswordModal(true);
							return;
						}
						handleConfirm(true, {
							skipPassword: true,
							skipPasswordReason: "Password setup skipped",
						});
					}}
					onPasswordAlreadySet={() => {
						const bookingUser = getBookingUserRecord(bookingDetails);
						const customerId = otherMembershipCreditPayment
							? othersMembershipClient?.user_id
							: bookingUser?.id ?? customerData?.id;
						setCheckoutCustomerIsPasswordSet(customerId, true);
						setCustomerData((prev) => ({
							...(prev || {}),
							is_password_set: true,
						}));
						setShowSetMembershipPasswordModal(false);
						setShowMembershipPasswordModal(true);
					}}
				/>
			)}

			{showSkipPasswordModal && (
				<CustomModal show={true} onHide={() => setShowSkipPasswordModal(false)} hideCloseButton={true} modalClass="skipPasswordOverrideModal">
					<SkipPasswordForMembership
						onClose={() => setShowSkipPasswordModal(false)}
						onSubmitted={(payload) => {
							setShowSkipPasswordModal(false);
							handleConfirm(true, {
								skipPassword: true,
								skipPasswordReason: payload?.reason || "Password setup skipped",
							});
						}}
						onEnterUserPassword={() => {
							setShowSkipPasswordModal(false);
							setShowSetMembershipPasswordModal(true);
						}}
					/>
				</CustomModal>
			)}
			<style>{`.skipPasswordOverrideModal { z-index: 10060 !important; }`}</style>

			{showNoPasswordPopup && (
				<MembershipNoPasswordPopup
					onClose={() => setShowNoPasswordPopup(false)}
					onUseWithoutPassword={() => {
						setShowNoPasswordPopup(false);
						handleConfirm(true, {
							skipPassword: true,
							skipPasswordReason: "No password configured – physical verification performed",
						});
					}}
				/>
			)}

			{showMembershipPasswordModal && (
				<MembershipPasswordPopup
					membershipId={resolveMembershipIdForPassword()}
					membershipUserId={resolveMembershipUserIdForPassword()}
					hideResetPassword={otherMembershipCreditPayment}
					onClose={() => setShowMembershipPasswordModal(false)}
					onSubmitted={(payload) => {
						if (
							payload &&
							typeof payload === "object" &&
							!Array.isArray(payload) &&
							payload.skip === true
						) {
							handleConfirm(true, {
								skipPassword: true,
								skipPasswordReason: payload.reason || "",
							});
						} else {
							handleConfirm(true, {
								skipPassword: false,
								skipPasswordReason: "",
							});
						}
					}}
				/>
			)}
		</div>
	);
};

export default ConfirmationPage;
